import { useCallback, useEffect, useState } from "react";
import type { DisplayMinAgeState } from "./display-min-age";
import {
  type DisplayMinAgeFetchResult,
  type DisplayMinAgeWriteResult,
  fetchDisplayMinAge,
  removeDisplayMinAge,
  saveDisplayMinAge,
} from "./display-min-age-api";

/** Исход записи окна: `invalid` — не повторять, `unavailable` — повторить можно. */
export type DisplayMinAgeOutcome = "ok" | "invalid" | "unavailable";

const LOADING: DisplayMinAgeState = { status: "loading" };

interface UseDisplayMinAgeOptions {
  onSessionInvalid: () => void;
}

export interface UseDisplayMinAgeResult {
  state: DisplayMinAgeState;
  /** Задать время до показа в секундах. Ответ сервера ложится в состояние. */
  save: (minAgeSec: number) => Promise<DisplayMinAgeOutcome>;
  /** Сбросить в «не задано». */
  remove: () => Promise<DisplayMinAgeOutcome>;
  /** Перечитать настройку — «Повторить» окна, когда чтение не удалось. */
  reload: () => Promise<void>;
}

/**
 * Состояние после чтения. Неудача поверх прочитанной настройки её не гасит;
 * протухшая сессия на первом чтении даёт `unavailable`, а не вечный `loading`.
 */
export function displayMinAgeStateFromFetch(
  result: DisplayMinAgeFetchResult,
  previous: DisplayMinAgeState,
): DisplayMinAgeState {
  if (result.status === "ok") {
    return { status: "ready", minAgeSec: result.view.min_age_sec };
  }
  return previous.status === "ready" ? previous : { status: "unavailable" };
}

/** Перед повторным чтением прочитанная настройка остаётся, иное состояние — «читаем». */
export function displayMinAgeStateForReload(previous: DisplayMinAgeState): DisplayMinAgeState {
  return previous.status === "ready" ? previous : LOADING;
}

/**
 * Итог записи или сброса: новое состояние, исход для окна и сигнал каркасу.
 * Любая неудача оставляет прежнюю настройку — она продолжает действовать.
 */
export function resolveDisplayMinAgeWrite(
  result: DisplayMinAgeWriteResult,
  previous: DisplayMinAgeState,
): { state: DisplayMinAgeState; outcome: DisplayMinAgeOutcome; sessionInvalid: boolean } {
  if (result.status === "ok") {
    return {
      state: { status: "ready", minAgeSec: result.view.min_age_sec },
      outcome: "ok",
      sessionInvalid: false,
    };
  }
  if (result.status === "unauthorized") {
    return { state: previous, outcome: "unavailable", sessionInvalid: true };
  }
  return { state: previous, outcome: result.status, sessionInvalid: false };
}

/**
 * Время до показа §82 одним числом пользователя. Один экземпляр на каркас
 * (`app-shell.tsx`): окно кнопки правит настройку, экран плотностей читает её
 * же и отбирает строки сразу, без второго запроса.
 */
export function useDisplayMinAge({
  onSessionInvalid,
}: UseDisplayMinAgeOptions): UseDisplayMinAgeResult {
  const [state, setState] = useState<DisplayMinAgeState>({ status: "loading" });

  const reload = useCallback(async (): Promise<void> => {
    setState(displayMinAgeStateForReload);
    const result = await fetchDisplayMinAge();
    setState((previous) => displayMinAgeStateFromFetch(result, previous));
    if (result.status === "unauthorized") {
      onSessionInvalid();
    }
  }, [onSessionInvalid]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const apply = useCallback(
    async (send: () => Promise<DisplayMinAgeWriteResult>): Promise<DisplayMinAgeOutcome> => {
      const result = await send();
      // Исход и сигнал от прежнего состояния не зависят; само состояние
      // берётся функциональным обновлением, без побочных эффектов внутри.
      const { outcome, sessionInvalid } = resolveDisplayMinAgeWrite(result, LOADING);
      setState((previous) => resolveDisplayMinAgeWrite(result, previous).state);
      if (sessionInvalid) {
        onSessionInvalid();
      }
      return outcome;
    },
    [onSessionInvalid],
  );

  const save = useCallback(
    (minAgeSec: number) => apply(() => saveDisplayMinAge(minAgeSec)),
    [apply],
  );
  const remove = useCallback(() => apply(removeDisplayMinAge), [apply]);

  return { state, save, remove, reload };
}
